import { surveyIds } from '@chiroup/core/constants/SurveyIds';
import { decorateChainTitle } from '@chiroup/core/functions/chainTitle';
import { RequestStep } from '@chiroup/core/types/Request.type';
import { SurveyBehavesAs } from '@chiroup/core/types/Survey.type';
import {
  ClipboardList as ClipboardDocumentListIcon,
  IdCard as IdentificationIcon,
  User as UserIcon,
} from 'lucide-react';

import { Icon } from '@chiroup/components/Icon';
import { Icons } from '@chiroup/components/icons';
import React from 'react';
import { RequestBootstrap } from '../../services/request.service';

// Shared step picker — split-panel layout (brand title on the left,
// task list on the right) used by both the public request flow and
// the kiosk. The card is parent-positioned: callers wrap it in a
// `<main>` (or `KioskFrame`) so the same component fits inside any
// shell. Each row shows completion state plus a kind-specific icon.

const verbiage = {
  en: {
    eyebrow: 'Welcome',
    title: 'What would you like to complete?',
    body: 'Tap any item below to fill it out. You can finish what’s left later from this same link.',
    complete: 'Completed',
    pending: 'Open',
    kindSurvey: 'Survey',
    kindConsent: 'Consent',
    kindInsurance: 'Insurance',
    kindBasicInfo: 'Patient information',
    kindUploadPhotoId: 'Photo ID',
    kindUploadInsuranceCard: 'Insurance card',
    allDoneTitle: 'All set!',
    allDoneBody: 'You’ve completed everything we needed. Thank you.',
    finish: 'Done',
    noItems: 'No pre-visit items are pending.',
    sendToContact: 'Send these to my phone or email',
  },
  es: {
    eyebrow: 'Bienvenido',
    title: '¿Qué desea completar?',
    body: 'Toque cualquier elemento para completarlo. Puede terminar lo que falte más tarde desde este mismo enlace.',
    complete: 'Completado',
    pending: 'Abierto',
    kindSurvey: 'Cuestionario',
    kindConsent: 'Consentimiento',
    kindInsurance: 'Seguro',
    kindBasicInfo: 'Información del paciente',
    kindUploadPhotoId: 'Identificación con foto',
    kindUploadInsuranceCard: 'Tarjeta de seguro',
    allDoneTitle: '¡Listo!',
    allDoneBody: 'Ha completado todo lo que necesitábamos. Gracias.',
    finish: 'Terminar',
    noItems: 'No hay elementos pendientes.',
    sendToContact: 'Enviar a mi teléfono o correo',
  },
};

type Verbiage = (typeof verbiage)['en'];

const defaultKindLabel = (type: string, v: Verbiage): string => {
  switch (type) {
    case 'survey':
      return v.kindSurvey;
    case 'consent':
      return v.kindConsent;
    case 'insurance':
      return v.kindInsurance;
    case 'basicInformation':
      return v.kindBasicInfo;
    case 'uploadPhotoId':
      return v.kindUploadPhotoId;
    case 'uploadInsuranceCard':
      return v.kindUploadInsuranceCard;
    default:
      return v.kindSurvey;
  }
};

const kindIcon = (type: string): React.ReactNode => {
  switch (type) {
    case 'survey':
      return <ClipboardDocumentListIcon className="size-5" />;
    case 'consent':
      return <Icon icon={Icons.fileText} className="size-5" />;
    case 'insurance':
    case 'uploadInsuranceCard':
      return <IdentificationIcon className="size-5" />;
    case 'basicInformation':
      return <UserIcon className="size-5" />;
    case 'uploadPhotoId':
      return <IdentificationIcon className="size-5" />;
    default:
      return <ClipboardDocumentListIcon className="size-5" />;
  }
};

type Props = {
  bootstrap: RequestBootstrap;
  primaryColor: string;
  onPickStep: (index: number) => void;
  onFinish: () => void;
  // Optional pre-filter (e.g. kiosk drops uploads). Defaults to
  // dropping disabled steps only.
  filterStep?: (step: RequestStep) => boolean;
  // When set AND not all items are complete, an exit button appears
  // in the brand panel labeled with this string. Used by the kiosk
  // to surface "Switch patient" mid-flow. Public requests omit this
  // and only show the finish button once everything's done.
  incompleteFinishLabel?: { en: string; es: string };
  // Kiosk-only: when set AND items are still pending, a secondary
  // action appears under the task list letting the patient forward a
  // link to their phone/email instead of finishing on the device.
  // The public request flow omits this (the patient already has the
  // link), so the button never renders there.
  onForward?: () => void;
};

const StepMenu: React.FC<Props> = ({
  bootstrap,
  primaryColor,
  onPickStep,
  onFinish,
  filterStep,
  incompleteFinishLabel,
  onForward,
}) => {
  const language: 'en' | 'es' = bootstrap.language === 'es' ? 'es' : 'en';
  const v = verbiage[language];

  const visibleSteps = bootstrap.steps
    .map((step, index) => ({ step, index }))
    .filter(({ step }) => !step.disabled && (filterStep ? filterStep(step) : true));

  const allComplete = visibleSteps.length > 0 && visibleSteps.every(({ step }) => !!step.complete);

  // ACCS chains return multiple instances of the same survey template
  // across submits; the backend can only see one at a time, so the "(2nd
  // complaint)" labeling has to happen here after the FE has merged the
  // bootstraps. Restricted to ACCS only — never label CCS, Oswestry,
  // NDI, etc. even when they appear more than once.
  const isAccsStep = (step: RequestStep): boolean => {
    if (step.type !== 'survey') return false;
    const survey = step.survey;
    if (!survey) return false;

    return (
      survey.id === surveyIds.abbreviatedCCS || survey.behavesAs === SurveyBehavesAs.AbbreviatedCCS
    );
  };

  const accsPositionByPatientSurveyId = new Map<string, number>();
  const accsTotalBySurveyId = new Map<string, number>();
  {
    const seenBySurveyId = new Map<string, number>();

    for (const { step } of visibleSteps) {
      if (!isAccsStep(step) || !step.patientSurveyId || !step.survey?.id) continue;
      const position = (seenBySurveyId.get(step.survey.id) ?? 0) + 1;
      seenBySurveyId.set(step.survey.id, position);
      accsPositionByPatientSurveyId.set(step.patientSurveyId, position);
    }

    for (const [surveyId, total] of seenBySurveyId) {
      accsTotalBySurveyId.set(surveyId, total);
    }
  }

  const titleFor = (step: RequestStep): string => {
    const base =
      language === 'es' && step.esTitle
        ? step.esTitle
        : step.title ||
          (step.type === 'survey' && step.survey?.title ? step.survey.title : '') ||
          defaultKindLabel(step.type, v);

    if (isAccsStep(step) && step.patientSurveyId && step.survey?.id) {
      const position = accsPositionByPatientSurveyId.get(step.patientSurveyId);
      const total = accsTotalBySurveyId.get(step.survey.id) ?? 0;

      if (position && position > 1 && total > 1) {
        return decorateChainTitle(base, position, language);
      }
    }

    return base;
  };

  const subtitleFor = (step: RequestStep): string | null => {
    if (language === 'es' && step.esDescription) return step.esDescription;
    if (step.description) return step.description;

    return null;
  };

  const incompleteLabel = incompleteFinishLabel?.[language];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_3fr] lg:min-h-[60vh] rounded-3xl overflow-hidden ring-1 ring-gray-950/5 dark:ring-white/10 bg-white dark:bg-white/5">
      {/* Brand-color title panel — mirrors the kiosk phone-entry split
          so every "list of steps" screen reads as one family. */}
      <div
        className="p-6 sm:p-8 [@media(max-height:820px)]:p-5 flex flex-col justify-between gap-6 text-white"
        style={{ backgroundColor: primaryColor }}
      >
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-white/75">
            {v.eyebrow}
          </p>
          <h1 className="mt-2 text-2xl sm:text-3xl font-bold tracking-tight leading-tight">
            {allComplete ? v.allDoneTitle : v.title}
          </h1>
          <p className="mt-3 text-base text-white/90 max-w-prose">
            {allComplete ? v.allDoneBody : v.body}
          </p>
        </div>
        {(allComplete || incompleteLabel) && (
          <button
            type="button"
            onClick={onFinish}
            className="hidden lg:inline-flex self-start items-center justify-center rounded-full px-5 py-2 text-sm font-semibold bg-white/15 hover:bg-white/25 text-white"
          >
            {allComplete ? v.finish : incompleteLabel}
          </button>
        )}
      </div>

      <div className="p-5 sm:p-7 [@media(max-height:820px)]:p-4 flex flex-col gap-4">
        {visibleSteps.length === 0 ? (
          <div className="portal-card p-6 text-center">
            <p className="text-sm text-gray-500 dark:text-gray-400">{v.noItems}</p>
          </div>
        ) : (
          <ul role="list" className="flex flex-col gap-2.5">
            {visibleSteps.map(({ step, index }) => {
              const isComplete = !!step.complete;
              const kindLabel = defaultKindLabel(step.type, v);

              return (
                <li key={`${step.type}-${index}`}>
                  <button
                    type="button"
                    onClick={() => onPickStep(index)}
                    className="group w-full flex items-start gap-3 text-left rounded-2xl px-5 py-4 portal-card hover:ring-2 hover:ring-[--ring-color] focus-visible:ring-2 focus-visible:ring-[--ring-color] focus-visible:outline-none transition-shadow"
                    style={{ ['--ring-color' as any]: primaryColor } as React.CSSProperties}
                  >
                    <span
                      className="size-9 rounded-xl flex items-center justify-center shrink-0 mt-0.5"
                      style={{ backgroundColor: `${primaryColor}18`, color: primaryColor }}
                      aria-hidden="true"
                    >
                      {isComplete ? (
                        <Icon icon={Icons.checkCircle} className="size-5" />
                      ) : (
                        kindIcon(step.type)
                      )}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                        {kindLabel}
                      </p>
                      <p
                        className={`mt-0.5 text-base font-semibold ${
                          isComplete
                            ? 'text-gray-500 dark:text-gray-400 line-through'
                            : 'text-gray-900 dark:text-gray-100'
                        }`}
                      >
                        {titleFor(step)}
                      </p>
                      {subtitleFor(step) && (
                        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 line-clamp-2">
                          {subtitleFor(step)}
                        </p>
                      )}
                    </div>
                    <span
                      className="text-xs font-semibold shrink-0 self-center"
                      style={{ color: isComplete ? undefined : primaryColor }}
                    >
                      {isComplete ? v.complete : v.pending}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        {onForward && !allComplete && visibleSteps.length > 0 && (
          <button
            type="button"
            onClick={onForward}
            className="self-center mt-1 inline-flex items-center justify-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold ring-1 hover:bg-[--brand-tint] transition-colors"
            style={
              {
                color: primaryColor,
                ['--brand-tint' as any]: `${primaryColor}12`,
                ['--tw-ring-color' as any]: `${primaryColor}40`,
              } as React.CSSProperties
            }
          >
            <Icon icon={Icons.send} className="size-4" />
            {v.sendToContact}
          </button>
        )}

        {(allComplete || incompleteLabel) && (
          <button
            type="button"
            onClick={onFinish}
            className="lg:hidden self-center mt-1 inline-flex items-center justify-center rounded-full px-6 py-2.5 text-sm font-semibold text-white"
            style={{ backgroundColor: primaryColor }}
          >
            {allComplete ? v.finish : incompleteLabel}
          </button>
        )}
      </div>
    </div>
  );
};

export default StepMenu;
